import type { CloudConfig } from './config';

const OAUTH_TRANSACTION_KEY = 'budget.cloud.oauth.transaction';
const OAUTH_SCOPES = 'openid email';

export type CognitoDiscovery = Readonly<{
  authorizationEndpoint: string;
  issuer: string;
  tokenEndpoint: string;
}>;

export type CognitoTokens = Readonly<{
  accessToken: string;
  expiresAt: number;
  idToken: string | null;
  refreshToken: string | null;
}>;

export type OAuthCallbackError =
  | 'authorization-denied'
  | 'invalid-callback'
  | 'invalid-state'
  | 'missing-transaction'
  | 'token-exchange-failed';

export class OAuthError extends Error {
  constructor(
    public readonly code: OAuthCallbackError,
    message: string,
  ) {
    super(message);
    this.name = 'OAuthError';
  }
}

type OAuthTransaction = Readonly<{
  codeVerifier: string;
  returnTo: string;
  state: string;
}>;

type StorageLike = Pick<Storage, 'getItem' | 'removeItem' | 'setItem'>;
type Fetch = typeof fetch;

export async function discoverCognito(
  config: CloudConfig,
  fetcher: Fetch = fetch,
): Promise<CognitoDiscovery> {
  const response = await fetcher(
    `${config.cognitoIssuer}/.well-known/openid-configuration`,
    { headers: { Accept: 'application/json' } },
  );
  if (!response.ok) {
    throw new OAuthError(
      'token-exchange-failed',
      `Cognito discovery failed with HTTP ${response.status}.`,
    );
  }

  const payload: unknown = await response.json();
  if (!isRecord(payload)) {
    throw new OAuthError(
      'token-exchange-failed',
      'Cognito discovery returned an invalid document.',
    );
  }

  const issuer = readHttpsUrl(payload.issuer);
  const authorizationEndpoint = readHttpsUrl(payload.authorization_endpoint);
  const tokenEndpoint = readHttpsUrl(payload.token_endpoint);
  if (
    issuer !== config.cognitoIssuer ||
    authorizationEndpoint === null ||
    tokenEndpoint === null
  ) {
    throw new OAuthError(
      'token-exchange-failed',
      'Cognito discovery endpoints did not match the configured issuer.',
    );
  }

  return { authorizationEndpoint, issuer, tokenEndpoint };
}

export async function createAuthorizationUrl(
  config: CloudConfig,
  discovery: CognitoDiscovery,
  returnTo: string,
  storage: StorageLike = sessionStorage,
  cryptoProvider: Crypto = crypto,
): Promise<string> {
  const codeVerifier = randomBase64Url(32, cryptoProvider);
  const state = randomBase64Url(24, cryptoProvider);
  const challenge = base64Url(
    new Uint8Array(
      await cryptoProvider.subtle.digest(
        'SHA-256',
        new TextEncoder().encode(codeVerifier),
      ),
    ),
  );

  const transaction: OAuthTransaction = {
    codeVerifier,
    returnTo: safeReturnPath(returnTo),
    state,
  };
  storage.setItem(OAUTH_TRANSACTION_KEY, JSON.stringify(transaction));

  const url = new URL(discovery.authorizationEndpoint);
  url.search = new URLSearchParams({
    client_id: config.appClientId,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    redirect_uri: config.callbackUrl,
    response_type: 'code',
    scope: OAUTH_SCOPES,
    state,
  }).toString();
  return url.href;
}

export async function exchangeAuthorizationCallback(
  config: CloudConfig,
  discovery: CognitoDiscovery,
  callbackUrl: string,
  storage: StorageLike = sessionStorage,
  fetcher: Fetch = fetch,
  now: () => number = Date.now,
): Promise<Readonly<{ returnTo: string; tokens: CognitoTokens }>> {
  const url = new URL(callbackUrl);
  const authorizationError = url.searchParams.get('error');
  if (authorizationError !== null) {
    storage.removeItem(OAUTH_TRANSACTION_KEY);
    throw new OAuthError(
      'authorization-denied',
      url.searchParams.get('error_description') ?? authorizationError,
    );
  }

  const code = url.searchParams.get('code');
  const state = url.searchParams.get('state');
  if (!code || !state) {
    throw new OAuthError(
      'invalid-callback',
      'The authorization callback is missing code or state.',
    );
  }

  const transaction = readTransaction(storage);
  if (transaction === null) {
    throw new OAuthError(
      'missing-transaction',
      'The authorization transaction is no longer available.',
    );
  }
  storage.removeItem(OAUTH_TRANSACTION_KEY);
  if (transaction.state !== state) {
    throw new OAuthError(
      'invalid-state',
      'The authorization callback state did not match.',
    );
  }

  const tokens = await requestTokens(
    discovery.tokenEndpoint,
    new URLSearchParams({
      client_id: config.appClientId,
      code,
      code_verifier: transaction.codeVerifier,
      grant_type: 'authorization_code',
      redirect_uri: config.callbackUrl,
    }),
    fetcher,
    now,
  );
  return { returnTo: transaction.returnTo, tokens };
}

export async function refreshCognitoTokens(
  config: CloudConfig,
  discovery: CognitoDiscovery,
  refreshToken: string,
  fetcher: Fetch = fetch,
  now: () => number = Date.now,
): Promise<CognitoTokens> {
  const refreshed = await requestTokens(
    discovery.tokenEndpoint,
    new URLSearchParams({
      client_id: config.appClientId,
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
    }),
    fetcher,
    now,
  );
  return { ...refreshed, refreshToken };
}

export function createLogoutUrl(
  config: CloudConfig,
  discovery: CognitoDiscovery,
): string {
  const url = new URL('/logout', discovery.authorizationEndpoint);
  url.search = new URLSearchParams({
    client_id: config.appClientId,
    logout_uri: config.logoutUrl,
  }).toString();
  return url.href;
}

async function requestTokens(
  tokenEndpoint: string,
  body: URLSearchParams,
  fetcher: Fetch,
  now: () => number,
): Promise<CognitoTokens> {
  const response = await fetcher(tokenEndpoint, {
    body,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    method: 'POST',
  });
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok || !isRecord(payload)) {
    throw new OAuthError(
      'token-exchange-failed',
      `Cognito token exchange failed with HTTP ${response.status}.`,
    );
  }

  const accessToken = readNonEmptyString(payload.access_token);
  const expiresIn = payload.expires_in;
  if (
    accessToken === null ||
    typeof expiresIn !== 'number' ||
    !Number.isFinite(expiresIn) ||
    expiresIn <= 0
  ) {
    throw new OAuthError(
      'token-exchange-failed',
      'Cognito token response is invalid.',
    );
  }

  return {
    accessToken,
    expiresAt: now() + expiresIn * 1000,
    idToken: readNonEmptyString(payload.id_token),
    refreshToken: readNonEmptyString(payload.refresh_token),
  };
}

function readTransaction(storage: StorageLike): OAuthTransaction | null {
  const raw = storage.getItem(OAUTH_TRANSACTION_KEY);
  if (raw === null) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (
      isRecord(value) &&
      typeof value.codeVerifier === 'string' &&
      typeof value.returnTo === 'string' &&
      typeof value.state === 'string'
    ) {
      return {
        codeVerifier: value.codeVerifier,
        returnTo: value.returnTo,
        state: value.state,
      };
    }
  } catch {
    return null;
  }
  return null;
}

function randomBase64Url(length: number, cryptoProvider: Crypto): string {
  const bytes = new Uint8Array(length);
  cryptoProvider.getRandomValues(bytes);
  return base64Url(bytes);
}

function base64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary)
    .replaceAll('+', '-')
    .replaceAll('/', '_')
    .replace(/=+$/, '');
}

function safeReturnPath(value: string): string {
  return value.startsWith('/') && !value.startsWith('//') ? value : '/cloud';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readNonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

function readHttpsUrl(value: unknown): string | null {
  const stringValue = readNonEmptyString(value);
  if (stringValue === null) return null;
  try {
    const url = new URL(stringValue);
    return url.protocol === 'https:' ? url.href.replace(/\/$/, '') : null;
  } catch {
    return null;
  }
}
